import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack
} from '@mui/material';
import {
  getGridDateOperators,
  getGridStringOperators,
  GridColDef,
  GridPaginationModel,
  GridRowSelectionModel
} from '@mui/x-data-grid';
import { Claim } from '../../../@types/organization';
import { Form, FormikProvider, getIn, useFormik } from 'formik';
import {
  ChangeEvent,
  Dispatch,
  FunctionComponent,
  SetStateAction,
  useEffect,
  useRef,
  useState
} from 'react';
import * as Yup from 'yup';
import { CustomServerDataGrid } from '../../../components/datagrid/Custom';
import { addUser, deleteUsers, getOrganizationUsers } from '../../../redux/slices/user';
import { RootState, useSelector } from '../../../redux/store';
import { capitalCase } from 'change-case';
import { useNavigate } from 'react-router';
import { PATH_DASHBOARD } from 'routes/paths';
import EmailConfirmed from './EmailConfirmed';
import useCustomDispatch from 'redux/dispatch';
import { getPermissions } from 'redux/slices/permissions';
import { getAssignableClaims } from 'redux/slices/organization';
import { DataGridKey } from 'redux/slices/datagrid';
import { useTranslator } from 'translation/useTranslator';
import { useDebounce } from 'hooks/useDebounce';
import { Button, fDate, TextField } from '@nps.today/component-library';
import UserAccessFields from './UserAccessFields';
import useAuth from 'hooks/useAuth';
import { isAccessible, MANAGEMENT_CLAIM_TYPES, rules } from 'access';

const containsOnlyFilterOperators = getGridStringOperators().filter(
  (operator) => operator.value === 'contains'
);

const dateIsFilterOperators = getGridDateOperators().filter((operator) => operator.value === 'is');

interface OrganizationUsersProps {
  selectedUsers: GridRowSelectionModel | undefined;
  setSelectedUsers: Dispatch<SetStateAction<GridRowSelectionModel | undefined>>;
  openAddUser: boolean;
  setOpenAddUser: Dispatch<SetStateAction<boolean>>;
  openDeleteUsers: boolean;
  setOpenDeleteUsers: Dispatch<SetStateAction<boolean>>;
}

export const OrganizationUsers: FunctionComponent<OrganizationUsersProps> = ({
  selectedUsers,
  setSelectedUsers,
  openAddUser,
  setOpenAddUser,
  openDeleteUsers,
  setOpenDeleteUsers
}) => {
  const translator = useTranslator();
  const customDispatch = useCustomDispatch();
  const navigate = useNavigate();
  const auth = useAuth();
  const DATA_GRID_KEY = DataGridKey.OrganizationUsers;
  const { users, usersTotal, loading } = useSelector((state: RootState) => state.user);
  const { sorting, filtering } = useSelector((state: RootState) => state.datagrid);
  const { assignableRoles, isLoading: isPermissionsLoading } = useSelector(
    (state: RootState) => state.permissions
  );
  const { organizationProfile, assignableClaims } = useSelector(
    (state: RootState) => state.organization
  );
  const capabilities = {
    isAiFeedbackEnabled: organizationProfile.isAiFeedbackEnabled ?? false,
    assignablePermissionClaims: assignableClaims?.claims ?? null
  };
  const canAssignClaims = isAccessible(
    rules.claimAssignment.any.claims ?? [],
    auth.user?.claims ?? []
  );

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [updateDataGrid, setUpdateDataGrid] = useState(0);
  const sortModel = sorting[DATA_GRID_KEY];
  const filterModel = filtering[DATA_GRID_KEY];
  const lastFetchedModelsRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    customDispatch({ action: getPermissions, disableSuccessMessage: true });
    customDispatch({ action: getAssignableClaims, disableSuccessMessage: true });
  }, [customDispatch]);

  useEffect(() => {
    const models = JSON.stringify({ sortModel, filterModel });
    const modelsChanged =
      lastFetchedModelsRef.current !== undefined && lastFetchedModelsRef.current !== models;
    lastFetchedModelsRef.current = models;
    if (modelsChanged && paginationModel.page !== 0) {
      setPaginationModel((current) => ({ ...current, page: 0 }));
      return;
    }
    customDispatch({
      action: getOrganizationUsers,
      actionParameters: {
        sortModel,
        filterModel,
        skip: paginationModel.page * paginationModel.pageSize,
        take: paginationModel.pageSize
      },
      disableSuccessMessage: true
    });
  }, [updateDataGrid, paginationModel, sortModel, filterModel, customDispatch]);

  const columns: GridColDef<any>[] = [
    {
      field: 'id',
      headerName: 'id',
      flex: 1,
      filterable: false
    },
    {
      field: 'name',
      hideable: false,
      headerName: translator.name(),
      flex: 1,
      filterOperators: containsOnlyFilterOperators
    },
    {
      field: 'userName',
      hideable: false,
      headerName: translator.username(),
      flex: 1,
      filterOperators: containsOnlyFilterOperators
    },
    {
      field: 'created',
      headerName: translator.created(),
      type: 'date',
      flex: 1,
      hideable: false,
      filterOperators: dateIsFilterOperators,
      valueGetter: (_value, row) => (row.created ? new Date(row.created) : null),
      renderCell: (params) => {
        const created = params.row.created as string;
        return fDate(created);
      }
    },
    {
      field: 'managementClaims',
      headerName: translator.management(),
      flex: 1,
      hideable: false,
      filterOperators: containsOnlyFilterOperators,
      valueGetter: (_value, row) => {
        const claims = row.claims as Claim[];
        const managementClaims = claims?.filter((claim: Claim) =>
          MANAGEMENT_CLAIM_TYPES.includes(claim.claimType)
        );
        const claimValues =
          managementClaims?.map((claim: Claim) => capitalCase(claim.claimValue)) || [];
        return claimValues.join(', ');
      }
    },
    {
      field: 'emailConfirmed',
      headerName: translator.emailConfirmed(),
      type: 'boolean',
      hideable: false,
      width: 150,
      disableColumnMenu: true,
      disableReorder: true,
      renderCell: (params) => {
        const userId = params.row.userId;
        const userEmail = params.row.email;
        const emailConfirmed = params.row.emailConfirmed;
        return (
          <EmailConfirmed userId={userId} userEmail={userEmail} emailConfirmed={emailConfirmed} />
        );
      }
    }
  ];

  let isLoading = loading.users || loading.user;

  const Schema = Yup.object().shape({
    email: Yup.string()
      .email(translator.invalidEmail())
      .required(translator.fieldRequiredError(translator.email())),
    role: Yup.string().required(translator.fieldRequiredError(translator.userAccessTemplate()))
  });

  let formik = useFormik({
    initialValues: {
      email: '',
      role: '',
      claims: [] as { claimType: string; claimValue: string }[]
    },
    enableReinitialize: true,
    validationSchema: Schema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      setOpenAddUser(false);
      customDispatch({
        action: addUser,
        actionParameters: values,
        onSuccess: () => {
          setUpdateDataGrid((count) => count + 1);
        },
        onFinally: () => {
          setSubmitting(false);
        }
      });
    }
  });

  const { errors, touched, handleSubmit, isSubmitting, getFieldProps, setFieldValue } = formik;

  const [emailInput, setEmailInput] = useState('');
  const [showPermissions, setShowPermissions] = useState(false);
  const debouncedEmail = useDebounce(emailInput, 750);

  useEffect(() => {
    setFieldValue('email', debouncedEmail);
  }, [debouncedEmail, setFieldValue]);

  const selectedUserIds = Array.from(selectedUsers?.ids ?? []);

  const deleteSelectedUsers = async () => {
    setOpenDeleteUsers(false);

    customDispatch({
      action: deleteUsers,
      actionParameters: selectedUserIds,
      onSuccess: () => {
        setOpenAddUser(false);
        setUpdateDataGrid((count) => count + 1);
      },
      onFinally: () => {
        setSelectedUsers({ type: 'include', ids: new Set() });
      }
    });
  };

  function DeleteUsersDialog() {
    return (
      <Dialog open={openDeleteUsers}>
        <DialogTitle>{translator.deleteUserTitle()}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {translator.deleteUsersDescription(`${selectedUserIds.length}`)}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            dataTestId="delete-users-dialog-cancel"
            color={'primary'}
            onClick={() => setOpenDeleteUsers(false)}
          >
            {translator.cancel()}
          </Button>
          <Button
            dataTestId="delete-users-dialog-delete"
            color={'error'}
            variant="contained"
            onClick={deleteSelectedUsers}
            autoFocus
          >
            {translator.delete()}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  const onRowClick = (userId: string) => {
    navigate(`${PATH_DASHBOARD.organization.users}/${userId}`);
  };

  const handleCloseAddUser = () => {
    setOpenAddUser(false);
    setShowPermissions(false);
    setEmailInput('');
    formik.resetForm();
  };

  const handleAccessTemplateChange = (templateRole: string, templateClaims: Claim[]) => {
    formik.setFieldValue('role', templateRole);
    formik.setFieldValue('claims', templateClaims);
  };

  return (
    <Stack>
      <Dialog onClose={handleCloseAddUser} open={openAddUser} maxWidth={'lg'} fullWidth>
        <DialogTitle>{translator.addNewUser()}</DialogTitle>
        <DialogContent>
          <FormikProvider value={formik}>
            <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
              <Box pt={2} />
              <Stack spacing={2}>
                <UserAccessFields
                  dataTestId="add-user-dialog"
                  role={getFieldProps('role').value}
                  claims={formik.values.claims}
                  capabilities={capabilities}
                  assignableRoles={assignableRoles}
                  canAssignClaims={canAssignClaims}
                  showPermissions={showPermissions}
                  disabled={isPermissionsLoading}
                  onRoleChange={handleAccessTemplateChange}
                  onClaimsChange={(updatedClaims) => formik.setFieldValue('claims', updatedClaims)}
                  error={
                    getIn(touched, 'role') && getIn(errors, 'role')
                      ? getIn(errors, 'role')
                      : undefined
                  }
                  userField={
                    <TextField
                      dataTestId="add-user-dialog-email"
                      label={translator.email()}
                      value={emailInput}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => setEmailInput(e.target.value)}
                      onBlur={() => formik.setFieldTouched('email', true)}
                      error={Boolean(getIn(errors, 'email') && getIn(touched, 'email'))}
                      helperText={getIn(touched, 'email') && getIn(errors, 'email')}
                    />
                  }
                />
              </Stack>
            </Form>
          </FormikProvider>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Button
            dataTestId="add-user-dialog-toggle-permissions"
            variant={'text'}
            onClick={() => setShowPermissions((current) => !current)}
          >
            {showPermissions ? translator.hidePermissions() : translator.viewPermissions()}
          </Button>
          <Stack direction="row" spacing={1}>
            <Button
              dataTestId="add-user-dialog-cancel"
              variant={'text'}
              onClick={handleCloseAddUser}
            >
              {translator.cancel()}
            </Button>
            <Button
              dataTestId="add-user-dialog-add"
              variant="contained"
              loading={isSubmitting}
              onClick={() => formik.submitForm()}
            >
              {translator.addUser()}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
      <CustomServerDataGrid
        setUpdateDataGrid={setUpdateDataGrid}
        dataGridKey={DATA_GRID_KEY}
        getRowId={(row) => row.userId}
        autoHeight
        loading={isLoading}
        checkboxSelection
        keepNonExistentRowsSelected
        rowSelectionModel={selectedUsers}
        onRowSelectionModelChange={(newSelectionModel) => {
          setSelectedUsers(newSelectionModel);
        }}
        disableRowSelectionOnClick
        onCellClick={(params) => {
          //if checkbox is clicked, return
          if (params.field === '__check__') return;

          onRowClick(params.id as string);
        }}
        rows={users.map((user) => {
          return { ...user, id: user.userId };
        })}
        columns={columns}
        rowCount={usersTotal}
        pagination
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={(model: GridPaginationModel) => {
          setPaginationModel((current) =>
            model.pageSize === current.pageSize ? model : { page: 0, pageSize: model.pageSize }
          );
        }}
      />
      <DeleteUsersDialog />
    </Stack>
  );
};
